import React from 'react';

import { useTranslation } from 'react-i18next';

import { debounce, keyBy, sortBy } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faInfoCircle } from '@fortawesome/pro-light-svg-icons';

import { Slider } from 'components/controls/Slider';
import NavigationButton from 'components/action/NavigationButton';
import { Button, Flex, Tooltip, Typography } from 'antd';
import Nav from 'components/controls/Nav';
import CommentEditionModal from 'components/Modal/CommentEditionModal';

import './OptionEvaluation.sass';

export const OptionEvaluation = (props: any) => {
	const { t } = useTranslation();

	const currentOption = props.options[props.optionIndex % props.options.length];
	const { constraints = [], indicators = [], stakefactors = [] } = props;


	const stakefactorEvaluations = currentOption ? keyBy(currentOption.stakefactorsEvaluations, 'itemId') : {};
	const indicatorEvaluations = currentOption ? keyBy(currentOption.indicatorsEvaluations, 'itemId') : {};
	const constraintEvaluations = currentOption ? keyBy(currentOption.constraintsEvaluations, 'itemId') : {};

	if (!props.options || props.options.length == 0) {
		return (
			<div className="OptionEvaluation">
				<Typography.Title level={2}>{t("models.option.no_x")}</Typography.Title>
			</div>
		);
	}

	const handleEditComment = (option, item, evaluation) => {
		props.pushModal(<CommentEditionModal
			option={option}
			item={item}
			values={{
				description: evaluation ? evaluation.description : ""
			}}
			onClose={() => {
				props.popModal();
			}}
			onSave={({ description }) => {
				props["onUpdate" + item.__typename + "Evaluation"](option.id, item.id, undefined, description, evaluation ? false : true);
				props.popModal();
			}}
		></CommentEditionModal>)
	}

	return (
		<div className="OptionEvaluation">
			<Typography.Title level={2} className="noselection">{t('pages.optionEvaluation.title')}</Typography.Title>
			<Typography.Title level={3} className="noselection">{t('pages.optionEvaluation.subtitle').split('#').map((line: string) => (
				<div key={line}>{line}</div>
			))}</Typography.Title>

			<Flex vertical={true} align="center">
				<div className='mt-4 mb-2 m-auto text-center' style={{ maxWidth: "800px", minWidth: "60vw" }}>
					<Nav
						fullWidth
						count={props.options.length}
						options={props.options}
						index={props.optionIndex}
						onSelect={(index: number) => {
							props.setOptionIndex(index);
						}}
					>
						<Typography.Title level={2}>{t("models.option.name")} {(props.optionIndex % props.options.length) + 1} (&ldquo;
							{props.options[props.optionIndex % props.options.length].name}&rdquo;) - {t("models.indicator.name_plural")}
							<Tooltip
								title={
									<div>
										<div>{t("pages.indicators.gauge.negative")}</div><br />
										<div>{t("pages.indicators.gauge.neutral")}</div><br />
										<div>{t("pages.indicators.gauge.positive")}</div>
									</div>
								}
							>
								<FontAwesomeIcon style={{ marginLeft: '0.25rem' }} icon={faInfoCircle}></FontAwesomeIcon>
							</Tooltip>
						</Typography.Title>
					</Nav>

					{sortBy(indicators, "name").map((s: any) => (
						<Flex vertical={true} justify="center" align="center" key={currentOption.id + '-indicators-' + s.id} className={['Evaluation', s.id, s.weight === 0 ? "header mb-1 mt-2" : "mb-1 mt-1"].join(" ")}>

							<div>
								{s.name}

								{s.description && <Tooltip title={s.description}>
									<FontAwesomeIcon className="ml-05" icon={faInfoCircle} />
								</Tooltip>}
							</div>

							<Flex justify="center" align="center" className="content text-center" style={{ width: "700px" }}>
								{s.weight !== 0 &&
									<>
										<Slider
											key={currentOption.id + '-indicators-' + s.id}
											onChange={debounce((value: number) => {
												props.onUpdateIndicatorEvaluation(currentOption.id, s.id, value, undefined);
											}, 250)}
											value={
												indicatorEvaluations[s.id] && indicatorEvaluations[s.id].value !== null
													? indicatorEvaluations[s.id].value
													: 50
											}
										></Slider>

										<Tooltip title={(indicatorEvaluations[s.id] && indicatorEvaluations[s.id].description) || "Comment your evaluation"}>
											<Button
												className={["comment", indicatorEvaluations[s.id] && indicatorEvaluations[s.id].description ? "filled" : "empty"].join(" ")}
												shape="circle"
												onClick={() => handleEditComment(currentOption, s, indicatorEvaluations[s.id])}
											>
												<FontAwesomeIcon icon={faComment} />
											</Button>
										</Tooltip>
									</>
								}

							</Flex>
						</Flex>
					))}
				</div>

				<br />
				<div className='mt-4 mb-2 m-auto text-center' style={{ maxWidth: "800px", minWidth: "60vw" }}>
					<Nav
						fullWidth
						count={props.options.length}
						options={props.options}
						index={props.optionIndex}
						onSelect={(index: number) => {
							props.setOptionIndex(index);
						}}
					>
						<Typography.Title level={2}>
							{t("models.option.name")} {(props.optionIndex % props.options.length) + 1} (&ldquo;
							{props.options[props.optionIndex % props.options.length].name}&rdquo;) - {t("models.constraint.name_plural")}
							<Tooltip
								title={
									<div>
										<div>{t("pages.constraints.gauge.negative")}</div><br />
										<div>{t("pages.constraints.gauge.neutral")}</div><br />
										<div>{t("pages.constraints.gauge.positive")}</div>
									</div>
								}
							>
								<FontAwesomeIcon style={{ marginLeft: '0.25rem' }} icon={faInfoCircle}></FontAwesomeIcon>
							</Tooltip>
						</Typography.Title>
					</Nav>
					<div className="OptionEvaluationConstraints mt-1 mb-2">
						{sortBy(constraints, "name").map((s: any) => (
							<Flex vertical={true} justify="center" align="center" key={currentOption.id + '-constraints-' + s.id} className={['Evaluation', s.id, s.weight === 0 ? "header mb-1 mt-2" : "mb-1"].join(" ")}>

								<div>
									{s.name}

									{s.description && <Tooltip title={s.description}>
										<FontAwesomeIcon className="ml-05" icon={faInfoCircle} />
									</Tooltip>}
								</div>


								<Flex justify="center" align="center" className="content text-center" style={{ width: "700px" }}>
									{s.weight !== 0 &&
										<>
											<Slider
												key={currentOption.id + '-constraints-' + s.id}
												onChange={debounce((value: number) => {
													props.onUpdateConstraintEvaluation(currentOption.id, s.id, value, undefined);
												}, 250)}
												value={
													constraintEvaluations[s.id] && constraintEvaluations[s.id].value !== null
														? constraintEvaluations[s.id].value
														: 50
												}
											></Slider>
											<Tooltip title={(constraintEvaluations[s.id] && constraintEvaluations[s.id].description) || "Comment your evaluation"}>
												<Button
													className={["comment", constraintEvaluations[s.id] && constraintEvaluations[s.id].description ? "filled" : "empty"].join(" ")}
													shape="circle"
													onClick={() => handleEditComment(currentOption, s, indicatorEvaluations[s.id])}
												>
													<FontAwesomeIcon icon={faComment} />
												</Button>
											</Tooltip>
										</>
									}

								</Flex>
							</Flex>
						))}
					</div>

					<br />
					<div className='mt-4 mb-2 m-auto text-center' style={{ maxWidth: "800px", minWidth: "60vw" }}>
						<Nav
							fullWidth
							count={props.options.length}
							options={props.options}
							index={props.optionIndex}
							onSelect={(index: number) => {
								props.setOptionIndex(index);
							}}
						>
							<Typography.Title level={2}>
								{t("models.option.name")} {(props.optionIndex % props.options.length) + 1} (&ldquo;
								{props.options[props.optionIndex % props.options.length].name}&rdquo;) - {t("models.stakefactor.name_plural")}
								<Tooltip
									title={
										<div>
											<div>{t("pages.stakefactors.gauge.negative")}</div><br />
											<div>{t("pages.stakefactors.gauge.neutral")}</div><br />
											<div>{t("pages.stakefactors.gauge.positive")}</div>
										</div>
									}
								>
									<FontAwesomeIcon style={{ marginLeft: '0.25rem' }} icon={faInfoCircle}></FontAwesomeIcon>
								</Tooltip>
							</Typography.Title>
						</Nav>
						<div key={props.optionIndex} className="mt-1" style={{ marginBottom: '2rem' }}>
							{sortBy(stakefactors, "name").map((s: any) => (
								<Flex vertical={true} justify="center" align="center" key={currentOption.id + '-' + s.id} className={['Evaluation text-center', s.id, s.weight === 0 ? "header mb-1 mt-2" : "mb-1"].join(" ")}>

									<div>
										{s.name}

										{s.description && <Tooltip title={s.description}>
											<FontAwesomeIcon className="ml-05" icon={faInfoCircle} />
										</Tooltip>}
									</div>

									<Flex justify="center" align="center" className="content text-center" style={{ width: "700px" }}>
										{s.weight !== 0 &&
											<>
												<Slider
													key={currentOption.id + '-stakefactors-' + s.id}
													onChange={debounce((value: number) => {
														props.onUpdateStakefactorEvaluation(currentOption.id, s.id, value, undefined);
													}, 250)}
													value={
														stakefactorEvaluations[s.id] && stakefactorEvaluations[s.id].value !== null
															? stakefactorEvaluations[s.id].value
															: 50
													}
												></Slider>
												<Tooltip title={(stakefactorEvaluations[s.id] && stakefactorEvaluations[s.id].description) || "Comment your evaluation"}>
													<Button
														className={["comment", stakefactorEvaluations[s.id] && stakefactorEvaluations[s.id].description ? "filled" : "empty"].join(" ")}
														shape="circle"
														onClick={() => handleEditComment(currentOption, s, indicatorEvaluations[s.id])}
													>
														<FontAwesomeIcon icon={faComment} />
													</Button>
												</Tooltip>
											</>
										}


									</Flex>
								</Flex>
							))}
						</div>
					</div>

					<Nav
						fullWidth
						count={props.options.length}
						options={props.options}
						index={props.optionIndex}
						onSelect={(index: number) => {
							props.setOptionIndex(index);
						}}
					>
						{props.options[props.optionIndex % props.options.length].name} - &nbsp;{t("models.option.name")} {(props.optionIndex % props.options.length) + 1} / {props.options.length}
					</Nav>
					<Flex justify="center" align="center" className="mt-4 mb-4">
						<NavigationButton onClick={() => props.onGoNext('/projects/' + props?.id + '/results')}>
							Next step
						</NavigationButton>
					</Flex>
				</div>
			</Flex>
		</div >
	);
};
